import { ChangeDetectorRef, Component, OnInit, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import * as L from 'leaflet';
import { environment } from '../../../environments/environment';

// Corrige l'icône par défaut Leaflet dans les builds Angular (sinon marqueur invisible).
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'assets/leaflet/marker-icon-2x.png',
  iconUrl: 'assets/leaflet/marker-icon.png',
  shadowUrl: 'assets/leaflet/marker-shadow.png'
});

@Component({
  selector: 'app-localisation',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './localisation.page.html',
  styleUrls: ['./localisation.page.scss']
})
export class LocalisationPage implements OnInit, AfterViewInit {
  private readonly apiBaseUrl = environment.apiBaseUrl;
  private readonly planLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors'
  });
  private readonly aerialLayer = L.tileLayer(
    'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    {
      attribution: 'Tiles © Esri'
    }
  );

  capteurs: any[] = [];
  selectedCapteur: any = null;
  loading = true;
  error = '';
  latitude: number | null = null;
  longitude: number | null = null;
  franceMetropolitaine = false;
  departement = '';
  coordinateInput = '';
  positionLoading = false;
  positionSuccess = '';
  positionError = '';
  map: any = null;
  marker: any = null;
  private layerControl: L.Control.Layers | null = null;

  adresse = '';
  geocodeError = '';

  constructor(
    private readonly http: HttpClient,
    private readonly cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.http.get<any>(`${this.apiBaseUrl}/api/capteurs`).subscribe({
      next: (res) => {
        this.capteurs = res.items || [];
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.error = 'Erreur lors du chargement des capteurs.';
        this.loading = false;
        this.cdr.markForCheck();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.latitude != null && this.longitude != null) {
      this.initMap();
    }
  }

  onCapteurChange(): void {
    this.latitude = null;
    this.longitude = null;
    this.franceMetropolitaine = false;
    this.departement = '';
    this.coordinateInput = '';
    this.positionSuccess = '';
    this.positionError = '';

    if (this.selectedCapteur && this.selectedCapteur.id) {
      this.positionLoading = true;
      this.http.get<any>(`${this.apiBaseUrl}/api/capteurs/${this.selectedCapteur.id}/getPosition`).subscribe({
        next: (res) => {
          this.latitude = res.latitude;
          this.longitude = res.longitude;
          this.franceMetropolitaine = !!res.france_metropolitaine;
          this.departement = res.departement ?? '';
          this.syncCoordinateInput();
          this.positionLoading = false;
          this.cdr.markForCheck();
          setTimeout(() => this.initMap(), 0);
        },
        error: () => {
          this.latitude = null;
          this.longitude = null;
          this.positionLoading = false;
          this.positionError = 'Impossible de charger la position.';
          this.cdr.markForCheck();
        }
      });
    } else if (this.map) {
      this.map.remove();
      this.map = null;
      this.marker = null;
    }
  }

  submitPosition(): void {
    if (!this.parseCoordinateInput()) {
      return;
    }

    if (!this.validateDepartement()) {
      return;
    }

    if (!this.selectedCapteur || !this.selectedCapteur.id || this.latitude == null || this.longitude == null) {
      return;
    }

    this.positionLoading = true;
    this.positionSuccess = '';
    this.positionError = '';

    this.http.post<any>(`${this.apiBaseUrl}/api/capteurs/${this.selectedCapteur.id}/setPosition`, {
      latitude: this.latitude,
      longitude: this.longitude,
      france_metropolitaine: this.franceMetropolitaine,
      departement: this.franceMetropolitaine ? this.departement.trim().toUpperCase() : null,
    }).subscribe({
      next: () => {
        this.positionLoading = false;
        this.positionSuccess = 'Position enregistrée.';
        this.cdr.markForCheck();
        setTimeout(() => this.initMap(), 0);
      },
      error: () => {
        this.positionLoading = false;
        this.positionError = "Erreur lors de l'enregistrement.";
        this.cdr.markForCheck();
      }
    });
  }

  async geocodeAdresse(): Promise<void> {
    this.geocodeError = '';
    if (!this.adresse || this.adresse.trim().length < 3) {
      this.geocodeError = 'Veuillez entrer une adresse valide.';
      return;
    }

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(this.adresse)}`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        credentials: 'omit',
      });

      if (!response.ok) {
        this.geocodeError = 'Erreur lors de la requête de géocodage.';
        this.cdr.markForCheck();
        return;
      }

      const results = (await response.json()) as Array<{ lat: string; lon: string }>;
      if (!results || results.length === 0) {
        this.geocodeError = 'Adresse introuvable.';
        this.cdr.markForCheck();
        return;
      }

      const result = results[0];
      const lat = parseFloat(result.lat);
      const lon = parseFloat(result.lon);
      if (isNaN(lat) || isNaN(lon)) {
        this.geocodeError = 'Coordonnées invalides pour cette adresse.';
        this.cdr.markForCheck();
        return;
      }

      this.latitude = lat;
      this.longitude = lon;
      this.syncCoordinateInput();

      if (this.marker) {
        this.marker.setLatLng([lat, lon]);
        this.map.setView([lat, lon], 15);
      } else if (this.map) {
        this.marker = L.marker([lat, lon], { draggable: true }).addTo(this.map);
        this.marker.on('dragend', () => {
          const pos = this.marker.getLatLng();
          this.latitude = pos.lat;
          this.longitude = pos.lng;
          this.cdr.markForCheck();
        });
      }

      this.cdr.markForCheck();
    } catch {
      this.geocodeError = 'Erreur lors de la requête de géocodage.';
      this.cdr.markForCheck();
    }
  }

  private initMap(): void {
    const lat = this.latitude ?? 0;
    const lng = this.longitude ?? 0;

    if (!this.map) {
      this.map = L.map('map').setView([lat, lng], 15);
      this.planLayer.addTo(this.map);
      this.layerControl = L.control.layers(
        {
          'Plan': this.planLayer,
          'Vue aérienne': this.aerialLayer,
        },
        undefined,
        { collapsed: false }
      ).addTo(this.map);

      this.marker = L.marker([lat, lng], { draggable: true }).addTo(this.map);
      this.marker.on('dragend', () => {
        const pos = this.marker.getLatLng();
        this.latitude = pos.lat;
        this.longitude = pos.lng;
        this.syncCoordinateInput();
        this.cdr.markForCheck();
      });
      return;
    }

    this.map.setView([lat, lng], 15);
    if (this.marker) {
      this.marker.setLatLng([lat, lng]);
    }
  }

  private syncCoordinateInput(): void {
    if (this.latitude == null || this.longitude == null) {
      this.coordinateInput = '';
      return;
    }
    this.coordinateInput = `${this.latitude.toFixed(6)}, ${this.longitude.toFixed(6)}`;
  }

  private parseCoordinateInput(): boolean {
    const normalized = this.coordinateInput.trim();
    if (!normalized) {
      this.positionError = 'Veuillez saisir des coordonnées au format 43.277, 5.405.';
      return false;
    }

    const match = normalized.match(/^(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)$/);
    if (!match) {
      this.positionError = 'Format attendu : 43.277, 5.405';
      return false;
    }

    const latitude = Number(match[1]);
    const longitude = Number(match[2]);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      this.positionError = 'Coordonnées invalides.';
      return false;
    }

    this.latitude = latitude;
    this.longitude = longitude;
    this.positionError = '';
    return true;
  }

  protected onFranceMetropolitaineChange(): void {
    if (!this.franceMetropolitaine) {
      this.departement = '';
    }
  }

  private validateDepartement(): boolean {
    if (!this.franceMetropolitaine) {
      return true;
    }

    const normalized = this.departement.trim().toUpperCase();
    const departementPattern = /^(?:0[1-9]|[1-8][0-9]|9[0-5]|2[A-B])$/;

    if (!normalized) {
      this.positionError = 'Le département est requis pour la France métropolitaine.';
      return false;
    }

    if (!departementPattern.test(normalized)) {
      this.positionError = 'Le département doit être valide pour la France métropolitaine (01 à 95, 2A ou 2B).';
      return false;
    }

    this.departement = normalized;
    return true;
  }
}
